/**
 * Shared Basket — recipient-side API services.
 * Extracted from SharedBasketPage.jsx.
 */
import { appConfig } from '../../../config/app-config';
import { CART_SHARED_BY_TOKEN, CUSTOMER_GROUPS, PRODUCTS_BY_ID } from '../../../shared/constants/api';
import * as apiClient from '../../../shared/services/apiClient';
import { CartSnapshot, CtCustomerGroup, CtProduct, SharedBasketResponse } from '../types/sharedBasket.types';

/** Fetch the immutable cart snapshot for a given share token. */
export async function loadBasketByToken(shareToken: string): Promise<CartSnapshot> {
  const response = await apiClient.get(CART_SHARED_BY_TOKEN(shareToken));

  if (response.status === 410) {
    throw Object.assign(new Error('This share link has expired'), { code: 'EXPIRED' });
  }
  if (response.status === 404) {
    throw Object.assign(new Error('Share link not found'), { code: 'NOT_FOUND' });
  }
  if (!response.ok) {
    throw new Error('Failed to load shared basket');
  }

  const data = (await response.json()) as SharedBasketResponse;

  if (!data.cartSnapshot?.lineItems) {
    throw new Error('Invalid cart snapshot structure');
  }

  return data.cartSnapshot;
}

/** Fetch customer groups and return a map of name → group object. */
export async function loadCustomerGroupMap(): Promise<Record<string, CtCustomerGroup>> {
  const map: Record<string, CtCustomerGroup> = {};

  try {
    const response = await apiClient.get(CUSTOMER_GROUPS);
    if (!response.ok) return map;

    const data = await response.json();
    const results: CtCustomerGroup[] = data.results ?? data;

    results.forEach((cg) => {
      const name =
        typeof cg.name === 'object'
          ? (cg.name as Record<string, string>)[appConfig.defaults.locale] ??
            (cg.name as Record<string, string>)['en']
          : cg.name;
      map[name] = cg;
    });
  } catch {
    // Ignore — caller will use default prices
  }

  return map;
}

/** Fetch live product data for a single product ID. Returns null on failure. */
export async function loadProductById(productId: string): Promise<CtProduct | null> {
  try {
    const response = await apiClient.get(PRODUCTS_BY_ID(productId), {
      expand: 'masterData.current.masterVariant.prices[*].discounted.discount',
    });
    if (!response.ok) return null;
    return response.json() as Promise<CtProduct>;
  } catch {
    return null;
  }
}
